import * as React from 'react';

import type {
  EnterpriseProperties,
  PageConfigComponent as PageConfigComponentTrackingData,
  Recommender,
} from '@coursera/event-pulse-types';

import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { getCollectionType } from 'bundles/collections-common/utils/collectionsUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import ProductCardPreviewImage from 'bundles/product-card/components/ProductCardPreviewImage';
import {
  convertDifficultyToEnum,
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';

type Props = {
  entity: CollectionEntity;
  entityIndex: number;
  collectionIndex: number;
  collection: CarouselCollection;
  eventingData?: {
    pageConfigComponent?: PageConfigComponentTrackingData;
    enterprise?: EnterpriseProperties;
  };
};

const CompactLearningProductCard = ({ entity, entityIndex, collectionIndex, collection, eventingData }: Props) => {
  return (
    <LearningProductCard
      isMetadataAboveProductRating={true}
      ImageComponent={
        <ProductCardPreviewImage
          productType={entity.productType}
          imageUrl={entity.imageUrl}
          variant="grid"
          imageParams={getImagePropOverrides(entity.partners ?? [], entity.imageUrl ?? '')}
        />
      }
      name={entity.name || ''}
      titleComponent="h3"
      linkProps={{
        href: entity?.link,
        target: entity.forceOpenInNewTab ? '_blank' : undefined,
        component: TrackedLink2,
      }}
      // CDS renders its "Multiple educators" byline whenever partners.length !== 1, and an empty
      // array still mounts the byline. Pass undefined so a product with no partner (e.g. a learning
      // path) gets no byline at all.
      partners={
        entity?.partners?.length
          ? getPartnerNamesAndLogos(entity.partners, !!entity?.coBrandingEnabled, entity.partnerLogos)
          : undefined
      }
      autocompleteProductCardData={{
        cardImageUrl: entity.imageUrl,
        partnerName: entity?.partners?.[0]?.name,
        partnerLogo: entity?.partnerLogos?.[0] ?? entity?.partners?.[0]?.logo,
        difficulty: convertDifficultyToEnum(entity?.productDifficultyLevel),
      }}
      avgProductRating={entity?.avgProductRating}
      numProductRatings={entity?.numProductRatings}
      productType={entity?.productType}
      variant="grid"
      statusTagConfig={{
        hideAllStatusTags: true,
      }}
      id={entity.id}
      slug={entity?.slug}
      cardIndex={entityIndex}
      trackingProps={{
        trackedLinkProps: {
          trackingName: 'collection_product_card',
          withVisibilityTracking: true,
          requireFullyVisible: true,
          data: {
            itemIndex: entityIndex,
            collectionId: collection?.collectionTrackingData?.collectionId,
            ...collection.collectionTrackingData,
            ...(entity.trackingData || { id: entity.id, item: entity }),
            collectionIndex:
              collectionIndex !== undefined ? collectionIndex : collection?.collectionTrackingData?.collectionIndex,
          },
        },
        additionalProductCardTrackingData: {
          pageConfigComponent: eventingData?.pageConfigComponent,
        },
        additionalV3TrackingData: {
          carousel: {
            id: collection?.collectionTrackingData?.id ?? collection?.collectionId ?? '',
            section:
              collectionIndex !== undefined ? collectionIndex : collection?.collectionTrackingData?.collectionIndex,
            model: collection?.collectionId as string,
            type: getCollectionType(collection?.collectionId as string),
            recommender: collection?.recommender as Recommender | undefined,
            name: collection?.title,
          },
          ...(eventingData?.enterprise && { enterprise: eventingData.enterprise }),
        },
      }}
      recommender={collection?.recommender}
    />
  );
};

export default CompactLearningProductCard;
